import { useState, useEffect, useRef, useCallback } from "react";
import apiSubscription from "../../services/subscription";

/**
 * Debounced tax number verification.
 *
 * Hits POST /subscriptionV2/verify-taxid while the user types, so a bad
 * number is caught before they reach the payment step — not after the
 * card has been charged.
 *
 * Why an API call instead of validating in the browser:
 *   Today the backend only runs a checksum, which the client could do
 *   itself. But the check is expected to grow (Revenue Department TIN
 *   lookup verifies the number actually exists, which is impossible
 *   client-side). Keeping the rule server-side means that upgrade needs
 *   no frontend change.
 *
 * Why debounced instead of on-submit (unlike useCheckBillingIdentity):
 *   Duplicate-checking is a yes/no gate that only matters at submit.
 *   A malformed number is a typo — the user wants to know while the
 *   field still has focus, not three steps later.
 *
 * Race handling: every run bumps a sequence number and stale responses
 * are dropped, so fast typing can't leave an old result on screen.
 *
 * @param {Object}  input
 * @param {string}  input.tax_number
 * @param {string}  input.detail_type   "company" | "personal"
 * @param {boolean} [input.enabled=true]
 * @param {number}  [input.delay=600]
 *
 * @returns {{
 *   checking: boolean,
 *   valid: boolean | null,   // null = ยังไม่ได้ตรวจ (ว่าง / ปิดใช้งาน / เรียกไม่ได้)
 *   message: string | null,
 *   verify: () => Promise<boolean>,  // เช็คทันทีไม่รอ debounce (ใช้ตอนกดปุ่ม)
 * }}
 */
export function useVerifyTaxId({
  tax_number,
  detail_type,
  enabled = true,
  delay = 600,
}) {
  const [state, setState] = useState({ checking: false, valid: null, message: null });
  const seqRef = useRef(0);

  useEffect(() => {
    const raw = String(tax_number ?? "").trim();

    // ยังไม่กรอก — อย่าเพิ่งขึ้นแดง ผู้ใช้ยังพิมพ์ไม่เสร็จ
    if (!enabled || !raw) {
      setState({ checking: false, valid: null, message: null });
      return;
    }

    const seq = ++seqRef.current;
    setState((s) => ({ ...s, checking: true }));

    const timer = setTimeout(async () => {
      try {
        const res = await apiSubscription.verifyTaxId({
          tax_number: raw,
          detail_type,
        });
        if (seq !== seqRef.current) return; // มี request ใหม่แซงแล้ว
        setState({
          checking: false,
          valid: res?.valid === true,
          message: res?.valid === true ? null : res?.message ?? null,
        });
      } catch {
        if (seq !== seqRef.current) return;
        // เรียกไม่ได้ → ไม่บล็อกผู้ใช้ ปล่อยให้ zod + backend กันตอน submit
        setState({ checking: false, valid: null, message: null });
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [tax_number, detail_type, enabled, delay]);

  /**
   * ตรวจทันทีโดยไม่รอ debounce — ใช้ตอนกดปุ่มถัดไป
   *
   * ถ้าผู้ใช้พิมพ์แล้วกดเร็วกว่า debounce (600ms) ผล state จะยังเป็น null
   * ปล่อยผ่านไปจะไปโดนปฏิเสธตอนจ่ายเงินแทน — เรียกสดตรงนี้กันเคสนั้น
   *
   * คืน true เมื่อ "ไปต่อได้" ซึ่งรวมกรณีเรียก API ไม่ได้ด้วย —
   * เน็ตมีปัญหาไม่ควรทำให้สมัครไม่ได้ (backend superRefine กันอีกชั้น)
   */
  const verify = useCallback(async () => {
    const raw = String(tax_number ?? "").trim();
    if (!enabled || !raw) return true;

    const seq = ++seqRef.current;
    setState((s) => ({ ...s, checking: true }));
    try {
      const res = await apiSubscription.verifyTaxId({ tax_number: raw, detail_type });
      const ok = res?.valid === true;
      if (seq === seqRef.current) {
        setState({ checking: false, valid: ok, message: ok ? null : res?.message ?? null });
      }
      return ok;
    } catch {
      if (seq === seqRef.current) setState({ checking: false, valid: null, message: null });
      return true; // เรียกไม่ได้ → ไม่บล็อก
    }
  }, [tax_number, detail_type, enabled]);

  return { ...state, verify };
}

export default useVerifyTaxId;
